(() => {
  const loader = document.currentScript;
  if (!loader) return;

  const loadedScripts = new Map();
  let commerceRequest;
  let searchRequest;

  const loadScript = (url, attributes = {}) => {
    if (!url) return Promise.resolve();
    if (loadedScripts.has(url)) return loadedScripts.get(url);

    const request = new Promise((resolve, reject) => {
      const absoluteUrl = new URL(url, window.location.href).href;
      const existing = Array.from(document.scripts).find((script) => script.src === absoluteUrl);
      if (existing) {
        if (existing.dataset.perfumiarzLoaded === 'true' || existing.readyState === 'complete') resolve();
        else {
          existing.addEventListener('load', resolve, { once: true });
          existing.addEventListener('error', reject, { once: true });
        }
        return;
      }

      const script = document.createElement('script');
      script.src = url;
      Object.entries(attributes).forEach(([name, value]) => script.setAttribute(name, value));
      script.addEventListener('load', () => {
        script.dataset.perfumiarzLoaded = 'true';
        resolve();
      }, { once: true });
      script.addEventListener('error', () => reject(new Error(`Unable to load ${url}`)), { once: true });
      document.head.appendChild(script);
    });

    loadedScripts.set(url, request);
    return request;
  };

  const waitForThemeRuntime = () => {
    if (customElements.get('modal-dialog')) return Promise.resolve();
    return customElements.whenDefined('modal-dialog');
  };

  const loadCommerce = () => {
    if (commerceRequest) return commerceRequest;

    commerceRequest = waitForThemeRuntime()
      .then(() => Promise.all([
        loadScript(loader.dataset.productFormScript),
        loadScript(loader.dataset.quickAddScript, { 'data-preview-script': loader.dataset.previewScript }),
        loadScript(loader.dataset.favoritesScript),
      ]))
      .catch((error) => {
        commerceRequest = null;
        throw error;
      });

    return commerceRequest;
  };

  const loadSearch = () => {
    if (searchRequest) return searchRequest;

    const existingStylesheet = Array.from(document.styleSheets).some((stylesheet) => stylesheet.href === loader.dataset.searchStyle);
    searchRequest = new Promise((resolve, reject) => {
      if (existingStylesheet) {
        resolve();
        return;
      }
      const stylesheet = document.createElement('link');
      stylesheet.rel = 'stylesheet';
      stylesheet.href = loader.dataset.searchStyle;
      stylesheet.addEventListener('load', resolve, { once: true });
      stylesheet.addEventListener('error', reject, { once: true });
      document.head.appendChild(stylesheet);
    })
      .then(() => loadScript(loader.dataset.searchScript))
      .catch((error) => {
        searchRequest = null;
        throw error;
      });

    return searchRequest;
  };

  const afterHeroPaint = () => new Promise((resolve) => {
    const hero = document.querySelector('.hero-carousel__image');
    let finished = false;
    let timeout;

    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimeout(timeout);
      hero?.removeEventListener('load', painted);
      hero?.removeEventListener('error', painted);
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    };
    const painted = () => finish();

    timeout = setTimeout(finish, 4500);
    if (!hero || hero.complete) finish();
    else {
      hero.addEventListener('load', painted, { once: true });
      hero.addEventListener('error', painted, { once: true });
    }
  });

  const commerceSelector = 'modal-opener button[data-product-url], [data-favorite-toggle]';
  const searchSelector = 'details-modal.hn-nav__search-details, .header__search, .header__icon--search, #HeaderSearch, .cloud-search-mobile, .instant-search-menu';

  document.addEventListener('click', (event) => {
    const target = event.target.closest(commerceSelector);
    if (!target) return;

    const isFavorite = target.matches('[data-favorite-toggle]');
    const featureReady = isFavorite
      ? window.__perfumiarzFavoritesBridgeLoaded === true
      : Boolean(customElements.get('quick-add-modal'));
    if (featureReady) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    loadCommerce()
      .then(() => target.isConnected && target.click())
      .catch((error) => console.error('[Homepage features] Commerce assets failed to load.', error));
  }, true);

  ['pointerover', 'focusin', 'touchstart'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      if (event.target.closest(commerceSelector)) loadCommerce().catch(() => {});
      if (event.target.closest(searchSelector)) loadSearch().catch(() => {});
    }, { capture: true, passive: eventName !== 'focusin' });
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest(searchSelector)) loadSearch().catch(() => {});
  }, true);

  const start = () => {
    const productSections = document.querySelectorAll('[data-perfumiarz-products]');
    if (!productSections.length) return;

    if (!('IntersectionObserver' in window)) {
      afterHeroPaint().then(loadCommerce).catch(() => {});
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      afterHeroPaint().then(loadCommerce).catch(() => {});
    });
    productSections.forEach((section) => observer.observe(section));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
